import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./kyc.module.css";
import { useNavigate } from "react-router-dom";
import Config from "../../service/Config";
import { CircularProgress, Box, TextField, Tooltip } from "@mui/material";
import {
  DataGrid,
  useGridApiRef,
  GridPreferencePanelsValue,
  getGridStringOperators,
  getGridSingleSelectOperators,
  gridPaginatedVisibleSortedGridRowIdsSelector,
} from "@mui/x-data-grid";

/* ------------------------------------------------------------------
 * Typography / colours – same tokens as Transaction
 * ------------------------------------------------------------------ */
const SARABUN = "var(--font-sarabun), sans-serif";
const SANSOURCE = "var(--font-source-sans-3), sans-serif";

const HEADER_COLOR = "#666666";
const TEXT_COLOR = "#4C4E64";
const ACCENT_COLOR = "#3246A9";
const MUTED_COLOR = "#8C8C8C";

/* ------------------------------------------------------------------
 * Grid sizing
 * ------------------------------------------------------------------ */
const GRID_ROW_HEIGHT = 74;
const GRID_HEADER_HEIGHT = 56;
const GRID_FOOTER_HEIGHT = 53;
const GRID_MAX_HEIGHT = "96vh";
const GRID_MIN_ROWS = 3;

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

// All records are loaded in ONE request; the grid paginates / searches /
// filters them, so the row count and rows per page always match what is shown.
const FETCH_ALL_LIMIT = 100000; // high enough to get every record in one call
const FETCH_CONCURRENCY = 5; // only used if the API caps `limit`

const CONTAINS_ONLY = getGridStringOperators().filter(
  (op) => op.value === "contains",
);
const IS_ONLY = getGridSingleSelectOperators().filter(
  (op) => op.value === "is",
);

const parseQuickFilter = (input) =>
  input.split(" ").filter((word) => word !== "");

const EMPTY_FILTER_MODEL = {
  items: [],
  quickFilterValues: [],
  quickFilterExcludeHiddenColumns: false,
};

const DEFAULT_HIDDEN_COLUMNS = {};

const TOKEN_EXPIRED_MESSAGE =
  "Token has expired and user status could not be verified";

/* ------------------------------------------------------------------
 * Panel styling (columns + filter panels are portaled)
 * ------------------------------------------------------------------ */
const PANEL_SX = {
  "& , & *": { fontFamily: SANSOURCE },
  "& .MuiDataGrid-panelWrapper": { fontFamily: SANSOURCE },
  "& .MuiFormControlLabel-label, & .MuiTypography-root": {
    fontFamily: SANSOURCE,
    fontSize: 14,
    fontWeight: 500,
    color: TEXT_COLOR,
    letterSpacing: 0,
    textTransform: "none",
  },
  "& .MuiInputBase-root, & .MuiInputBase-input, & .MuiFormLabel-root, & .MuiSelect-select":
    {
      fontFamily: SANSOURCE,
      fontSize: 14,
      color: TEXT_COLOR,
    },
  "& .MuiInputBase-input::placeholder": {
    fontFamily: SANSOURCE,
    color: MUTED_COLOR,
    opacity: 1,
  },
  "& .MuiDataGrid-panelFooter .MuiButton-root": {
    fontFamily: SANSOURCE,
    fontSize: 13,
    fontWeight: 600,
    textTransform: "none",
    color: ACCENT_COLOR,
  },
  "& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate":
    {
      color: ACCENT_COLOR,
    },
  "& .MuiPaper-root, & .MuiDataGrid-paper": {
    borderRadius: "var(--br-xs)",
    overflow: "hidden",
  },
  "& .MuiDataGrid-panelWrapper": {
    borderRadius: "var(--br-xs)",
    overflow: "hidden",
  },
};

const SELECT_MENU_SX = {
  borderRadius: "var(--br-xs)",
  "& .MuiMenuItem-root": {
    fontFamily: SARABUN,
    fontSize: 14,
    fontWeight: 500,
    color: TEXT_COLOR,
    letterSpacing: 0,
    textTransform: "none",
  },
  "& .MuiMenuItem-root.Mui-selected": {
    color: ACCENT_COLOR,
    backgroundColor: "rgba(50, 70, 169, 0.08)",
  },
  "& .MuiMenuItem-root.Mui-selected:hover": {
    backgroundColor: "rgba(50, 70, 169, 0.12)",
  },
};

const ellipsisCell = (value) => (
  <Tooltip title={value || ""}>
    <span
      style={{
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </span>
  </Tooltip>
);

/* ------------------------------------------------------------------
 * Filter dropdown options — only values that exist in the loaded records
 * ------------------------------------------------------------------ */
const EMPTY_VALUE = "—";

const cleanValue = (v) => {
  const s = v === null || v === undefined ? "" : String(v).trim();
  return s === "" ? EMPTY_VALUE : s;
};

const buildValueOptions = (rows, field) =>
  [...new Set(rows.map((row) => row[field]))].sort((a, b) => {
    if (a === EMPTY_VALUE) return 1;
    if (b === EMPTY_VALUE) return -1;
    return a.localeCompare(b);
  });

const Kyc = ({ title }) => {
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const columnsButtonRef = useRef(null);

  // data + ui
  const [allItems, setAllItems] = useState([]); // every record from the API
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // DataGrid controls
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterModel, setFilterModel] = useState(EMPTY_FILTER_MODEL);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    DEFAULT_HIDDEN_COLUMNS,
  );
  const [panelAnchorEl, setPanelAnchorEl] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  // rows actually visible on the current page (after filters + pagination)
  const [visibleRowCount, setVisibleRowCount] = useState(0);

  const API_BASE = Config.kyc_api_endpoint;

  const goToFirstPage = useCallback(
    () => setPaginationModel((m) => (m.page === 0 ? m : { ...m, page: 0 })),
    [],
  );

  /* ----------------------------------------------------------------
   * Shared fetch helper (Cognito tokens)
   * ---------------------------------------------------------------- */
  const fetchKycPage = useCallback(
    async (pageNo, limit, signal) => {
      let parsedTokens = null;
      try {
        const tokens = localStorage.getItem("authToken");
        parsedTokens = tokens ? JSON.parse(tokens) : null;
      } catch {
        parsedTokens = null;
      }

      if (!parsedTokens?.IdToken || !parsedTokens?.AccessToken) {
        const err = new Error("Not authenticated");
        err.code = "NO_AUTH";
        throw err;
      }

      const res = await fetch(`${API_BASE}/advanced-kyc/all-data`, {
        method: "POST",
        signal,
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": process.env.REACT_APP_CUPI_API_KEY,
          aws_cognito_id_token: parsedTokens.IdToken,
          aws_cognito_access_token: parsedTokens.AccessToken,
        },
        body: JSON.stringify({ page: pageNo, limit }),
      });

      const json = await res.json();

      if (!res.ok || !json.success) {
        throw new Error(json?.error || "Request failed");
      }

      return {
        data: Array.isArray(json.data) ? json.data : [],
        total: Number(json?.meta?.total ?? 0),
      };
    },
    [API_BASE],
  );

  const handleFetchError = useCallback(
    (e) => {
      if (e.name === "AbortError") return;

      if (e.code === "NO_AUTH" || e.message === TOKEN_EXPIRED_MESSAGE) {
        localStorage.removeItem("username");
        localStorage.removeItem("email");
        localStorage.removeItem("authToken");
        navigate("/");
        return;
      }

      setError(e.message || "Something went wrong");
    },
    [navigate],
  );

  /* ----------------------------------------------------------------
   * Load every record once (and again on Reload)
   * ---------------------------------------------------------------- */
  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError("");
      try {
        // 1 request: ask for everything at once
        const first = await fetchKycPage(1, FETCH_ALL_LIMIT, controller.signal);
        const all = [...first.data];

        // Safety net, normally skipped: if the API caps `limit` and returned
        // fewer rows than `total`, fetch the remaining pages using the page
        // size the API actually honoured.
        const perPage = first.data.length;
        if (perPage > 0 && perPage < first.total) {
          const totalPages = Math.ceil(first.total / perPage);
          for (let p = 2; p <= totalPages; p += FETCH_CONCURRENCY) {
            const batch = [];
            for (let q = p; q < p + FETCH_CONCURRENCY && q <= totalPages; q++) {
              batch.push(fetchKycPage(q, perPage, controller.signal));
            }
            const results = await Promise.all(batch);
            results.forEach((r) => all.push(...r.data));
          }
        }

        setAllItems(all);
      } catch (e) {
        handleFetchError(e);
        if (e.name !== "AbortError") setAllItems([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [fetchKycPage, handleFetchError, refresh]);

  /* ----------------------------------------------------------------
   * Debounced search text — restart from page 1 on every new search
   * ---------------------------------------------------------------- */
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
      goToFirstPage();
    }, 250);
    return () => clearTimeout(timeout);
  }, [searchText, goToFirstPage]);

  // quick filter is driven from state (not apiRef) so it survives remounts
  const effectiveFilterModel = useMemo(
    () => ({
      ...filterModel,
      quickFilterValues: parseQuickFilter(debouncedSearch),
    }),
    [filterModel, debouncedSearch],
  );

  /* ----------------------------------------------------------------
   * Grid rows
   * ---------------------------------------------------------------- */
  const gridRows = useMemo(
    () =>
      allItems.map((row, index) => ({
        ...row,
        // index keeps ids unique even if two records share company + date
        id: `${row.company_id || "row"}-${row.created_at || ""}-${index}`,
        __date: row.created_at
          ? new Date(row.created_at).toLocaleDateString()
          : EMPTY_VALUE,
        __name: cleanValue(row.name),
        __companyName: cleanValue(row.company_name),
        __entityType: cleanValue(row.entity_type),
        __status: cleanValue(row.status),
      })),
    [allItems],
  );

  const statusOptions = useMemo(
    () => buildValueOptions(gridRows, "__status"),
    [gridRows],
  );
  const entityTypeOptions = useMemo(
    () => buildValueOptions(gridRows, "__entityType"),
    [gridRows],
  );

  const columns = useMemo(
    () => [
      {
        field: "__date",
        headerName: "DATE",
        flex: 0.9,
        minWidth: 100,
        headerAlign: "center",
        align: "center",
        filterOperators: CONTAINS_ONLY,
        // sort by the real timestamp, not the formatted string
        sortComparator: (_v1, _v2, p1, p2) => {
          const a = new Date(p1.api.getRow(p1.id)?.created_at || 0).getTime();
          const b = new Date(p2.api.getRow(p2.id)?.created_at || 0).getTime();
          return a - b;
        },
      },
      {
        field: "__name",
        headerName: "COMPANY REP.",
        flex: 1.2,
        minWidth: 140,
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "__companyName",
        headerName: "COMPANY NAME",
        flex: 1.4,
        minWidth: 160,
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "__entityType",
        headerName: "BUSINESS TYPE",
        flex: 1.1,
        minWidth: 130,
        type: "singleSelect",
        valueOptions: entityTypeOptions,
        filterOperators: IS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "__status",
        headerName: "STATUS",
        flex: 1,
        minWidth: 120,
        headerAlign: "center",
        align: "center",
        type: "singleSelect",
        valueOptions: statusOptions,
        filterOperators: IS_ONLY,
        renderCell: (params) => (
          <div className={styles.data1}>{params.value}</div>
        ),
      },
      {
        field: "__action",
        headerName: "ACTION",
        flex: 1.1,
        minWidth: 120,
        sortable: false,
        filterable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <button
            className={styles.button}
            onClick={() =>
              navigate(
                `/content/kyc/${params.row.company_id}/${encodeURIComponent(
                  params.row.entity_type || "",
                )}`,
              )
            }
          >
            <img alt="" src="/eye.svg" className={styles.btn_img} />
            View More
          </button>
        ),
      },
    ],
    [statusOptions, entityTypeOptions, navigate],
  );

  const allColumnsHidden = useMemo(
    () => columns.every((col) => columnVisibilityModel[col.field] === false),
    [columns, columnVisibilityModel],
  );

  /* ----------------------------------------------------------------
   * Height fits the rows actually shown on the current page
   * ---------------------------------------------------------------- */
  const syncVisibleRowCount = () => {
    if (!apiRef.current) return;
    const count = gridPaginatedVisibleSortedGridRowIdsSelector(apiRef).length;
    setVisibleRowCount((prev) => (prev === count ? prev : count));
  };

  const rowsOnPage =
    Math.min(visibleRowCount, paginationModel.pageSize) || GRID_MIN_ROWS;
  const gridHeight = `min(${GRID_MAX_HEIGHT}, ${
    GRID_HEADER_HEIGHT + GRID_FOOTER_HEIGHT + rowsOnPage * GRID_ROW_HEIGHT
  }px)`;

  /* ----------------------------------------------------------------
   * Toolbar helpers
   * ---------------------------------------------------------------- */
  const handleSearchChange = (e) => setSearchText(e.target.value);

  const toggleSearch = () => {
    setSearchOpen((open) => {
      if (open) setSearchText("");
      return !open;
    });
  };

  const openFilterPanel = (event) => {
    setPanelAnchorEl(event.currentTarget);
    apiRef.current?.showFilterPanel();
  };

  const openColumnsPanel = (event) => {
    setPanelAnchorEl(columnsButtonRef.current ?? event?.currentTarget ?? null);
    apiRef.current?.showPreferences(GridPreferencePanelsValue.columns);
  };

  const handleFilterModelChange = (model) => {
    // quick filter values are owned by debouncedSearch; keep only the rest
    setFilterModel({ ...model, quickFilterValues: [] });
    goToFirstPage();
  };

  const handlePaginationChange = (model) => {
    setPaginationModel((prev) =>
      model.pageSize !== prev.pageSize
        ? { page: 0, pageSize: model.pageSize } // restart after size change
        : model,
    );
  };

  const resetGridFilters = () => {
    setSearchText("");
    setDebouncedSearch("");
    setSearchOpen(false);
    setFilterModel(EMPTY_FILTER_MODEL);
    setColumnVisibilityModel(DEFAULT_HIDDEN_COLUMNS);
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  };

  return (
    <>
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(255,255,255,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <CircularProgress size={60} sx={{ color: "#2348AB" }} />
        </div>
      )}

      <div className={styles.frameGroup}>
        <div className={styles.feeStatementDataTable}>
          {/* ── top bar ─────────────────────────────────────────── */}
          <div className={styles.top}>
            <div className={styles.filterWrapper}>
              <div className={styles.filter}>
                <div className={styles.containertext}>
                  {title || "Company KYC (Advanced)"}
                </div>
              </div>

              <div className={styles.filter}>
                {searchOpen && (
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="Search name, company, type, status…"
                    value={searchText}
                    onChange={handleSearchChange}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") toggleSearch();
                    }}
                    sx={{
                      minWidth: 240,
                      mr: 1,
                      "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                      "& .MuiInputBase-input": {
                        fontFamily: SARABUN,
                        fontSize: 14,
                        color: TEXT_COLOR,
                      },
                      "& .MuiInputBase-input::placeholder": {
                        fontFamily: SARABUN,
                        color: MUTED_COLOR,
                        opacity: 1,
                      },
                    }}
                  />
                )}

                <button
                  className={styles.reLoadTable}
                  onClick={toggleSearch}
                  title="Search"
                >
                  <img
                    className={styles.vectorIcon1}
                    alt="Search"
                    src="/SearchSVG.svg"
                  />
                </button>

                <button
                  ref={columnsButtonRef}
                  className={styles.reLoadTable}
                  onClick={openColumnsPanel}
                  title="Show / hide columns"
                >
                  <img
                    className={styles.vectorIcon1}
                    alt="Columns"
                    src="/ThreeHorizontalLine.svg"
                  />
                </button>

                <button
                  className={styles.reLoadTable}
                  onClick={openFilterPanel}
                  title="Filter"
                >
                  <img
                    className={styles.vectorIcon1}
                    alt="Filter"
                    src="/ThreeVerticalline.svg"
                  />
                </button>

                <button
                  className={styles.reLoadTable}
                  onClick={() => {
                    resetGridFilters();
                    setRefresh((r) => !r);
                  }}
                  title="Reload"
                >
                  <img
                    className={styles.vectorIcon12}
                    alt="Reload"
                    src="/vector7.svg"
                  />
                </button>
              </div>
            </div>
            <div className={styles.topChild} />
          </div>

          {error && (
            <div style={{ color: "#B00020", padding: 12 }}>Error: {error}</div>
          )}

          {/* ── DataGrid ───────────────────────────────────────── */}
          {!loading && (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                minWidth: 0,
                height: gridHeight,
                ...(allColumnsHidden && { minHeight: 280 }),
                overflow: "hidden",
                fontFamily: SARABUN,
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                  borderBottom: "none",
                  overflow: "hidden",
                },
                "& .MuiDataGrid-footerContainer": { border: "none" },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
              }}
            >
              <DataGrid
                apiRef={apiRef}
                rows={gridRows}
                rowHeight={GRID_ROW_HEIGHT}
                columnHeaderHeight={GRID_HEADER_HEIGHT}
                columns={columns}
                disableColumnMenu
                disableRowSelectionOnClick
                filterModel={effectiveFilterModel}
                onFilterModelChange={handleFilterModelChange}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                pagination
                paginationMode="client"
                filterMode="client"
                sortingMode="client"
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationChange}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                initialState={{
                  sorting: { sortModel: [{ field: "__date", sort: "desc" }] },
                }}
                onStateChange={syncVisibleRowCount}
                slotProps={{
                  baseSelect: {
                    MenuProps: { PaperProps: { sx: SELECT_MENU_SX } },
                  },
                  panel: {
                    anchorEl: panelAnchorEl,
                    placement: "bottom-end",
                    sx: PANEL_SX,
                  },
                  filterPanel: {
                    filterFormProps: {
                      logicOperatorInputProps: { sx: { display: "none" } },
                      operatorInputProps: { sx: { display: "none" } },
                      columnInputProps: { sx: { minWidth: 160 } },
                      valueInputProps: {
                        InputComponentProps: { sx: { minWidth: 200 } },
                      },
                    },
                    sx: {
                      "& .MuiDataGrid-filterFormLogicOperatorInput": {
                        display: "none",
                      },
                      "& .MuiDataGrid-panelWrapper": { minWidth: 460 },
                      "& .MuiDataGrid-panelContent": { overflowX: "auto" },
                      "& .MuiDataGrid-filterForm": {
                        p: 2,
                        gap: 1,
                        alignItems: "flex-end",
                        flexWrap: "wrap",
                      },
                      "& .MuiDataGrid-filterFormDeleteIcon": {
                        flexShrink: 0,
                        alignSelf: "flex-end",
                      },
                    },
                  },
                }}
                localeText={{
                  noRowsLabel: "No records found.",
                  noResultsOverlayLabel:
                    "No records match this search or filter.",
                  filterPanelColumns: "Column",
                  filterPanelInputLabel: "Value",
                  filterPanelInputPlaceholder: "Filter value",
                }}
                sx={{
                  height: "100%",
                  width: "100%",
                  maxWidth: "100%",
                  border: "none",
                  fontFamily: SARABUN,
                  color: TEXT_COLOR,

                  "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-cell, & .MuiDataGrid-cell *, & .MuiDataGrid-overlay, & .MuiDataGrid-footerContainer, & .MuiTablePagination-root":
                    {
                      fontFamily: SARABUN,
                    },

                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f7f8fa",
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 600,
                    fontSize: 14,
                    letterSpacing: 0.4,
                    color: HEADER_COLOR,
                  },
                  "& .MuiDataGrid-columnSeparator": { display: "none" },
                  "& .MuiDataGrid-cell": {
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "none",
                    overflow: "hidden",
                    fontSize: 14,
                  },
                  "& .MuiDataGrid-footerContainer": { borderTop: "none" },
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                    {
                      outline: "none",
                    },
                }}
              />

              {allColumnsHidden && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                    backgroundColor: "#fff",
                    zIndex: 2,
                  }}
                >
                  <span
                    style={{
                      fontSize: 14,
                      color: "#667085",
                      fontFamily: SARABUN,
                    }}
                  >
                    All columns are hidden.
                  </span>
                  <button className={styles.button} onClick={openColumnsPanel}>
                    Manage Columns
                  </button>
                </div>
              )}
            </Box>
          )}
        </div>
      </div>
    </>
  );
};

export default Kyc;